import { useId, useLayoutEffect, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import type { Review, StockStatus } from './types';
import { averageRating, faceAvatars, featuredReview, formatPrice, reviewCountLabel } from './format';
import { Stars } from './Stars';
import { StoreImage } from '../../ui/StoreImage';

const STOCK: Record<StockStatus, { label: string; tone: 'green' | 'amber' | 'red' | 'grey' }> = {
  in_stock: { label: 'I lager', tone: 'green' },
  low: { label: 'Få kvar', tone: 'amber' },
  very_low: { label: 'Nästan slut', tone: 'red' },
  out_of_stock: { label: 'Slut i lager', tone: 'grey' },
};

export function StockStatusBadge({ status }: { status: StockStatus }) {
  const s = STOCK[status];
  return (
    <p className={`cn-stock cn-stock--${s.tone}`}>
      <span className="cn-stock__dot" aria-hidden="true" />
      {s.label}
    </p>
  );
}

/** Visar de första raderna. "Läs mer" fäller ut resten. Knappen syns bara om texten är längre än så. */
export function ExpandableText({ text, lines = 3 }: { text: string; lines?: number }) {
  const ref = useRef<HTMLParagraphElement>(null);
  const [open, setOpen] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const id = useId();

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || open) return;
    const check = () => setOverflows(el.scrollHeight > el.clientHeight + 1);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [text, open]);

  return (
    <div className="cn-expand">
      <p
        id={id}
        ref={ref}
        className={open ? 'cn-expand__text' : 'cn-expand__text is-clamped'}
        style={{ '--cn-lines': lines } as CSSProperties}
      >
        {text}
      </p>
      {(overflows || open) && (
        <button type="button" className="cn-expand__toggle" aria-expanded={open} aria-controls={id} onClick={() => setOpen((o) => !o)}>
          {open ? 'Visa mindre' : 'Läs mer'}
        </button>
      )}
    </div>
  );
}

interface Props {
  name: string;
  price: number;
  description: string;
  stockStatus?: StockStatus;
  reviews: Review[];
  /** Öppnar recensionsvyn (ReviewsSheet) */
  onOpenReviews: () => void;
  /** Butikens egna delar: säsongstagg över namnet, raden under namnet, paketets jämförpris och nedräkningen. */
  eyebrow?: ReactNode;
  subtitle?: ReactNode;
  priceNote?: ReactNode;
  afterStock?: ReactNode;
}

/** Namn, pris, citat, ansikten + stjärnor + antal recensioner, lagerstatus och beskrivning. */
export function ProductInfo({ name, price, description, stockStatus, reviews, onOpenReviews, eyebrow, subtitle, priceNote, afterStock }: Props) {
  const quote = featuredReview(reviews);
  const faces = faceAvatars(reviews, 4);
  const avg = averageRating(reviews);

  return (
    <section className="cn-info">
      {eyebrow}
      <div>
        <h1 className="cn-info__title">{name}</h1>
        {subtitle && <p className="cn-info__sub">{subtitle}</p>}
      </div>
      <div className="cn-info__pricing">
        <p className="cn-info__price">{formatPrice(price)}</p>
        {priceNote}
      </div>

      {quote && (
        <figure className="cn-quote">
          <blockquote>”{quote.text.trim()}”</blockquote>
          <figcaption>{quote.name}</figcaption>
        </figure>
      )}

      {reviews.length > 0 && (
        <div className="cn-proof">
          {faces.length > 0 && (
            <span className="cn-faces" aria-hidden="true">
              {faces.map((f) => (
                <StoreImage key={f.id} src={f.url} width={128} alt="" loading="lazy" decoding="async" />
              ))}
            </span>
          )}
          <Stars rating={avg} />
          <button type="button" className="cn-proof__link" onClick={onOpenReviews} aria-haspopup="dialog">
            {reviewCountLabel(reviews.length)}
          </button>
        </div>
      )}

      {(stockStatus || afterStock) && (
        <div className="cn-info__status">
          {stockStatus && <StockStatusBadge status={stockStatus} />}
          {afterStock}
        </div>
      )}

      {description.trim() && <ExpandableText text={description} />}
    </section>
  );
}
